import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { profileService } from '@/services/profileService'
import { UserProfile, UpdateProfileRequest } from '@/types/profile'

// Query keys for cache management
export const profileKeys = {
  all: ['profile'] as const,
  detail: () => [...profileKeys.all, 'detail'] as const,
}

/**
 * Hook for fetching user profile with React Query
 * - Automatic caching (5 min stale time)
 * - Automatic deduplication of requests
 * - Shared cache across components
 */
export const useProfile = () => {
  const queryClient = useQueryClient()
  const [uploadingPicture, setUploadingPicture] = useState(false)

  const {
    data: profile,
    isLoading: loading,
    error,
    refetch
  } = useQuery({
    queryKey: profileKeys.detail(),
    queryFn: () => profileService.getProfile(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const updateMutation = useMutation({
    mutationFn: (profileData: UpdateProfileRequest) => 
      profileService.updateProfile(profileData),
    onSuccess: (updatedProfile) => {
      // Update cache with new profile data
      queryClient.setQueryData(profileKeys.detail(), updatedProfile)
    },
  })

  const updateProfile = async (profileData: UpdateProfileRequest): Promise<UserProfile> => {
    return updateMutation.mutateAsync(profileData)
  }

  /**
   * Upload profile picture
   * Supports JPEG, PNG, WebP up to 25MB
   */
  const uploadProfilePicture = async (file: File): Promise<UserProfile> => {
    setUploadingPicture(true)
    try {
      const updatedProfile = await profileService.uploadProfilePicture(file)
      // Update cache with new profile data (includes new profile_picture_url)
      queryClient.setQueryData(profileKeys.detail(), updatedProfile)
      return updatedProfile
    } finally {
      setUploadingPicture(false)
    }
  }

  return {
    profile: profile ?? null,
    loading,
    updating: updateMutation.isPending,
    uploadingPicture,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    fetchProfile: refetch,
    updateProfile,
    uploadProfilePicture,
    refetch
  }
}
